import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import { decodeJwt, type JwtClaims } from '../api/jwt';

// Контракт TokenPair из OpenAPI.
export interface TokenPair {
  access_token: string;
  refresh_token: string;
  token_type: 'bearer';
  expires_in: number;
}

export interface AuthUser {
  id: string;
  /** Отсутствует в account-only сессии (V1.3-a: личность без оптики — Vision). */
  tenantId?: string;
  /** Отсутствует в account-only сессии (V1.3-a). */
  role?: 'admin' | 'employee' | 'platform_admin';
  /** UNIX seconds expiry access_token (для отображения / решения о refresh). */
  exp: number;
  /** Имя пользователя (claim `name`), если бэкенд его отдал; иначе undefined. */
  name?: string;
  /** Email аккаунта (claim `email`), если есть парольная личность. */
  email?: string;
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  setTokens: (pair: TokenPair) => void;
  logout: () => void;
  isAuthenticated: () => boolean;
}

function userFromClaims(claims: JwtClaims): AuthUser {
  return {
    id: claims.sub,
    tenantId: claims.tid,
    role: claims.role,
    exp: claims.exp,
    name: claims.name,
    email: claims.email || undefined,
  };
}

// Zustand store с persist в localStorage. Слушатели API-клиента и роутера
// читают этот store напрямую, авто-апдейтя UI при logout/setTokens.
//
// NB про ключ persist `sl-auth` (решение V1.3-b): Light и Vision живут на
// РАЗНЫХ origin (dev: 5173/5174; prod: light.starling.kz /
// starling-vision.com), поэтому их localStorage не пересекается и один ключ
// безопасен. Если когда-нибудь оба клиента окажутся на одном origin — сессии
// смешаются (tenant-токен Light ⟷ account-only токен Vision); тогда store И
// api-клиент (module-singleton, читает этот store) придётся превратить в
// фабрику `createAuthStore/createApiClient({storageKey})`. Сейчас это была бы
// спекулятивная сложность.
export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      setTokens: (pair) => {
        const claims = decodeJwt(pair.access_token);
        set({
          accessToken: pair.access_token,
          refreshToken: pair.refresh_token,
          user: claims ? userFromClaims(claims) : null,
        });
      },
      logout: () => {
        set({ accessToken: null, refreshToken: null, user: null });
      },
      isAuthenticated: () => {
        const { accessToken, user } = get();
        if (!accessToken || !user) return false;
        // Не валидируем expiry строго — refresh-loop в api/client.ts разрулит истёкший access.
        return true;
      },
    }),
    {
      name: 'sl-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
      }),
    },
  ),
);

// Хелперы для использования вне React-компонентов (например, в api/client).
export const authStore = {
  getState: useAuthStore.getState,
  setState: useAuthStore.setState,
  subscribe: useAuthStore.subscribe,
};
